
































































































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import List from '@/components/util/List.vue';
import BuildingDisplayComponent from '@/components/system/BuildingDisplayComponent.vue';
import StartStopTimer from '@/components/orders/StartStopTimer.vue';

import { ColumnDisplayOption } from '@/models/ColumnDisplayOption';
import { SortChangedEvent } from '@/models/SortChangedEvent';

import {
  WorkordersApi,
  Workorder,
  PerformoSettings,
  QueryWorkOrdersRequest,
  Property,
  WorkorderPagedCollectionApiResponse
} from 'performo-api';
import { mapGetters } from 'vuex';
import { User } from 'oidc-client';
import { handleApiError } from '@/ApiHelpers';

@Component({
  components: {
    List,
    BuildingDisplayComponent,
    StartStopTimer
  },
  computed: mapGetters({
    currentPropertyId: 'currentPropertyId',
    currentUser: 'currentUser',
    api: 'ordersApi',
    settings: 'settings',
    currentProperty: 'currentProperty'
  })
})
export default class WorkOrderListView extends Vue {
  @Prop({ required: true, default: {} }) query!: QueryWorkOrdersRequest;
  @Prop({ required: false }) columnDisplay!: ColumnDisplayOption[];
  @Prop({ required: false }) outputMode!: string;
  @Prop({ required: false, default: false }) doPagination!: boolean;
  @Prop({ required: false, default: false }) updateRouteWithQuery!: boolean;
  @Prop({ required: false, default: 'No work orders to display.' })
  zeroCountDisplayMessage!: string;
  @Prop({ required: false, type: Number, default: 25 }) pageSize!: number;

  private api!: WorkordersApi;
  private settings!: PerformoSettings;
  private currentProperty!: Property;

  private currentPropertyId!: string;
  private currentUser!: User;

  private pagedOrders!: WorkorderPagedCollectionApiResponse;
  private updatingRouteQuery = false;

  data() {
    return {
      pagedOrders: undefined
    };
  }

  get columnDisplayComputed(): ColumnDisplayOption[] {
    // if we have a columnDisplay prop, use that, its overriding the system default for a reason.
    if (this.columnDisplay) {
      return this.columnDisplay;
    }
    // finally if we have a system column set, convert to column option and use that.
    const systemColumnDisplay = this.settings
      .workOrderListColumnOptions as ColumnDisplayOption[];
    if (systemColumnDisplay && systemColumnDisplay.length) {
      return systemColumnDisplay;
    }
    // finally use the default column options for the component.
    return [
      { displayName: 'Order #', columnName: 'orderNumber', width: '85px' },
      {
        displayName: this.settings.multiPropLabelSingular || 'Property',
        columnName: 'propertyId',
        width: '75px'
      },
      { displayName: 'Assign To', columnName: 'assignTo' },
      {
        displayName: 'Start Date',
        columnName: 'startDateTime',
        dateFormatKind: 1,
        width: '105px'
      },
      {
        displayName: 'Completion Date',
        columnName: 'completionDateTime',
        dateFormatKind: 1,
        width: '140px'
      },
      {
        displayName: this.currentProperty.location1Label || 'Building',
        columnName: 'building',
        width: '175px'
      },
      {
        displayName: this.currentProperty.location2Label || 'Room',
        columnName: 'suite',
        width: '75px'
      },
      { displayName: 'Priority', columnName: 'jobPriority', width: '75px' },
      { displayName: 'Status', columnName: 'orderStatus', width: '75px' },
      {
        displayName: 'Timer',
        columnName: 'laborTimerAction',
        width: '100px',
        dontSort: true
      }
    ];
  }

  get ordersQuery() {
    return this.query;
  }
  get orders() {
    if (this.pagedOrders && this.pagedOrders.data) {
      return this.pagedOrders.data;
    }
    return undefined;
  }

  private sortChangedHandler(sort: SortChangedEvent) {
    this.$emit('sort-changed', sort);

    this.ordersQuery.sortColumn = sort.column;
    this.ordersQuery.sortDirection =
      sort.direction === 1 ? 'ascend' : 'descend';

    // reset to first page
    this.ordersQuery.skip = 0;
  }

  private orderStatusClass(item: Workorder) {
    if (item.orderNumber === 'REQ') {
      return 'submitted';
    } else {
      if (item.orderStatus) {
        return item.orderStatus.toLowerCase().replace(' ', '');
      }
      return '';
    }
  }

  private async paginationChangeHandler(pageNumber: number) {
    this.$emit('update:pagination', pageNumber);
  }

  private listRowClickedHandler(item: Workorder) {
    this.$emit('workorder-clicked', item);
  }

  @Watch('ordersQuery', { deep: true })
  private async queryChanged(val: QueryWorkOrdersRequest) {
    if (this.updatingRouteQuery) return;
    if (val) {
      if (this.updateRouteWithQuery) {
        await this.updateRouteQuery();
      } else {
        await this.fetchOrders();
      }
    }
  }

  @Watch('currentPropertyId')
  private async propertyChanged() {
    if (this.updateRouteWithQuery) {
      await this.updateRouteQuery();
    } else {
      await this.fetchOrders();
    }
  }

  private async updateRouteQuery() {
    this.updatingRouteQuery = true;
    const query = {
      ...this.ordersQuery,
      propertyId: this.currentPropertyId,
      skip: this.ordersQuery.skip,
      take: this.ordersQuery.take ? this.ordersQuery.take : this.pageSize,
      sortColumn:
        this.ordersQuery.sortColumn !== '' ? this.ordersQuery.sortColumn : '',
      sortDirection:
        this.ordersQuery.sortDirection !== ''
          ? this.ordersQuery.sortDirection
          : 'ascend'
    };

    if (
      this.$router.currentRoute.path === '/orders' &&
      Object.keys(this.$router.currentRoute.query).length === 0
    ) {
      // replace the router query if we're on the initial /orders landing page
      // which prevents a double back button click being necessary to back out
      // of the list screen.
      await this.$router
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .replace({ name: 'work-orders', query: query as any })
        .catch(() => {
          /*empty-ignore-route-to-same-url-errors*/
        });
    } else {
      // else we already have query parameters, continue pushing onto the back
      // button stack, so that users can back through their queries and filters.
      await this.$router
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .push({ name: 'work-orders', query: query as any })
        .catch(() => {
          /*empty-ignore-route-to-same-url-errors*/
        });
    }

    await this.fetchOrders();

    this.updatingRouteQuery = false;
  }

  private async fetchOrders() {
    if (this.ordersQuery) {
      const query = {
        ...this.ordersQuery,
        propertyId: this.currentPropertyId,
        skip: this.ordersQuery.skip ? this.ordersQuery.skip : 0,
        take: this.ordersQuery.take ? this.ordersQuery.take : this.pageSize,
        sortColumn:
          this.ordersQuery.sortColumn !== '' ? this.ordersQuery.sortColumn : '',
        sortDirection:
          this.ordersQuery.sortDirection !== ''
            ? this.ordersQuery.sortDirection
            : 'ascend'
      };

      try {
        this.pagedOrders = await this.api.queryWorkOrders(query);
      } catch (error) {
        await handleApiError(error);
      }

      this.$emit('updated', this.pagedOrders);
    }
  }

  private async mounted() {
    // load data
    if (this.updateRouteWithQuery) {
      await this.updateRouteQuery();
    } else {
      await this.fetchOrders();
    }
  }

  private userHasInterface(interfaceModule: string): boolean {
    if (!this.currentUser.profile) {
      return false;
    } // bail out if we can't read the profile object
    if (Array.isArray(this.currentUser.profile.interface)) {
      return this.currentUser.profile.interface.includes(interfaceModule);
    }
    return this.currentUser.profile.interface === interfaceModule;
  }
}
